import { useMemo, useState } from 'react'
import { useQuery } from '@apollo/client'
import { GET_ROLES, GET_ACTIVE_USERS } from '../../graphql/queries'
import type { AudienceRule } from '../../types/announcement'

type Mode = 'all' | 'custom'

interface Props {
  value: AudienceRule
  onChange: (rule: AudienceRule) => void
}

// Build a rule from the current selections, omitting empty dimensions so the
// server sees a clean { roles } / { users } / { roles, users } union.
const buildRule = (roles: number[], users: number[]): AudienceRule => {
  const rule: AudienceRule = {}
  if (roles.length) rule.roles = roles
  if (users.length) rule.users = users
  return rule
}

// Initial mode from the incoming rule shape; tracked in local state after mount
// so an empty custom selection doesn't snap back to "Everyone".
const initialMode = (rule: AudienceRule): Mode =>
  (rule.roles?.length || rule.users?.length) ? 'custom' : 'all'

const AudiencePicker = ({ value, onChange }: Props) => {
  const [mode, setModeState] = useState<Mode>(() => initialMode(value))
  const [userSearch, setUserSearch] = useState('')

  const { data: rolesData } = useQuery(GET_ROLES, { variables: { filters: { active: true } } })
  const { data: usersData } = useQuery(GET_ACTIVE_USERS)

  const roles: { id: number; role: string }[] = rolesData?.roles ?? []
  const users: { id: number; name: string }[] = usersData?.activeUsers ?? []

  const selectedRoles = value.roles ?? []
  const selectedUsers = value.users ?? []

  const filteredUsers = useMemo(() => {
    const q = userSearch.trim().toLowerCase()
    return q ? users.filter((u) => u.name.toLowerCase().includes(q)) : users
  }, [users, userSearch])

  const setMode = (next: Mode) => {
    setModeState(next)
    onChange(next === 'all' ? { all: true } : buildRule(selectedRoles, selectedUsers))
  }

  const toggleRole = (id: number) => {
    const next = selectedRoles.includes(id)
      ? selectedRoles.filter((r) => r !== id)
      : [...selectedRoles, id]
    onChange(buildRule(next, selectedUsers))
  }

  const toggleUser = (id: number) => {
    const next = selectedUsers.includes(id)
      ? selectedUsers.filter((u) => u !== id)
      : [...selectedUsers, id]
    onChange(buildRule(selectedRoles, next))
  }

  return (
    <div className="space-y-3">
      {/* Mode toggle */}
      <div className="flex flex-wrap gap-2">
        {([
          { key: 'all', label: 'Everyone', hint: 'All active staff' },
          { key: 'custom', label: 'Choose recipients', hint: 'By role and/or specific people' },
        ] as { key: Mode; label: string; hint: string }[]).map((m) => (
          <button
            key={m.key}
            type="button"
            onClick={() => setMode(m.key)}
            className={`rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors ${
              mode === m.key
                ? 'border-primary-500 bg-primary-50 text-primary-800'
                : 'border-secondary-200 bg-white text-gray-600 hover:bg-gray-50'
            }`}
            title={m.hint}
          >
            {m.label}
          </button>
        ))}
      </div>

      {mode === 'all' && (
        <p className="text-xs text-gray-500">Every active staff member receives this. New hires are included automatically.</p>
      )}

      {mode === 'custom' && (
        <div className="space-y-4">
          <p className="text-xs text-gray-500">
            Recipients are everyone in the selected roles, <strong>plus</strong> any individually selected staff. Roles re-resolve at publish (new hires included); pick individuals only for genuinely hand-picked additions.
          </p>

          {/* Roles */}
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Roles</p>
            <div className="rounded-lg border border-secondary-200 p-3 space-y-1.5 max-h-40 overflow-y-auto">
              {roles.length === 0 ? (
                <p className="text-sm text-gray-400">No roles available.</p>
              ) : roles.map((r) => (
                <label key={r.id} className="flex items-center gap-2 cursor-pointer text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={selectedRoles.includes(r.id)}
                    onChange={() => toggleRole(r.id)}
                    className="rounded border-gray-300 text-primary-500 focus:ring-primary-500"
                  />
                  {r.role}
                </label>
              ))}
            </div>
          </div>

          {/* Specific people */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Specific people</p>
              <span className="text-xs text-gray-400">{selectedUsers.length} selected</span>
            </div>
            <input
              type="text"
              value={userSearch}
              onChange={(e) => setUserSearch(e.target.value)}
              placeholder="Search staff…"
              className="input w-full max-w-xs mb-2"
            />
            <div className="rounded-lg border border-secondary-200 p-3 space-y-1.5 max-h-40 overflow-y-auto">
              {filteredUsers.length === 0 ? (
                <p className="text-sm text-gray-400">No staff match.</p>
              ) : filteredUsers.map((u) => (
                <label key={u.id} className="flex items-center gap-2 cursor-pointer text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={selectedUsers.includes(u.id)}
                    onChange={() => toggleUser(u.id)}
                    className="rounded border-gray-300 text-primary-500 focus:ring-primary-500"
                  />
                  {u.name}
                </label>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default AudiencePicker
